import { Injectable, signal, computed, inject, effect } from '@angular/core';
import { Observable, firstValueFrom } from 'rxjs';
import { ApiError } from '../../abstract/api-error';
import { BaseService } from '../../abstract/base.service';
import { CancelWorkOrderDto } from '../request-models/cancel-work-order-dto.model';
import { CreateWorkOrderDto } from '../request-models/create-work-order-dto.model';
import { UpdateWorkOrderDto } from '../request-models/update-work-order-dto.model';
import { WorkOrder } from '../data-models/work-order.model';
import { WorkOrderListFilters } from '../request-models/work-order-list-filters.model';
import { toWorkOrderRequest } from '../utils/work-order-request-utils';
import { OrganizationContextService } from '../services/organization-context.service';

/**
 * Work orders: `/work-orders[/:id[/cancel]]` (hub `user-work-orders-controller.ts`). The hub scopes every
 * route to the organization the interceptor sends.
 *
 * Holds a signal-backed cache of the active org's work orders so the list view stays reactive across
 * creates, updates and WS-driven refreshes. Mutations return `null` on failure and set `error` to the
 * hub's message.
 *
 * Reference: agent-os/specs/2026-04-30-marketplace-work-orders/spec.md
 */
@Injectable({ providedIn: 'root' })
export class WorkOrderService extends BaseService<WorkOrder> {
  private organizationContext = inject(OrganizationContextService);

  private workOrdersSignal = signal<WorkOrder[]>([]);
  private loadingSignal = signal<boolean>(false);
  private errorSignal = signal<string | null>(null);

  readonly workOrders = computed(() => this.workOrdersSignal());
  readonly isLoading = computed(() => this.loadingSignal());
  readonly error = computed(() => this.errorSignal());

  constructor() {
    super('work-orders', WorkOrder);
    effect(() => {
      const orgId = this.organizationContext.currentOrganizationId();
      if (orgId) {
        void this.loadAll();
      } else {
        this.workOrdersSignal.set([]);
      }
    });
  }

  /** Load the org's work orders into `workOrders()`. Failures set `error` instead of throwing. */
  async loadAll(filters: WorkOrderListFilters = {}): Promise<void> {
    if (!this.organizationContext.getCurrentOrganizationId()) return;
    this.loadingSignal.set(true);
    this.errorSignal.set(null);
    try {
      const query = { ...filters, page: filters.page ?? 1, limit: filters.limit ?? 100 };
      const page = await firstValueFrom(this.getPage({ query }));
      this.workOrdersSignal.set(page.items);
    } catch (error: unknown) {
      this.errorSignal.set(ApiError.fromHttpError(error).errors[0] ?? 'Failed to load work orders');
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /** Single work-order read; the detail screen's primary entry point. `null` when missing or failed. */
  async fetchOne(id: string): Promise<WorkOrder | null> {
    try {
      return this.cached(await firstValueFrom(this.get(id)));
    } catch (error: unknown) {
      console.error('Error fetching work order:', ApiError.fromHttpError(error).message);
      return null;
    }
  }

  async createWorkOrder(dto: CreateWorkOrderDto): Promise<WorkOrder | null> {
    return this.mutate(this.create(toWorkOrderRequest(dto)), 'Failed to create work order');
  }

  async updateWorkOrder(id: string, dto: UpdateWorkOrderDto): Promise<WorkOrder | null> {
    return this.mutate(this.update(id, toWorkOrderRequest(dto)), 'Failed to update work order');
  }

  async cancel(id: string, dto: CancelWorkOrderDto): Promise<WorkOrder | null> {
    const cancelled = this.request('PATCH', { id, path: 'cancel' }, data => this.toWorkOrder(data), { reason: dto.reason });
    return this.mutate(cancelled, 'Failed to cancel work order');
  }

  /** Drop a work order from the local cache (used by `work-order.deleted`). */
  removeFromCache(id: string): void {
    this.workOrdersSignal.update((list) => list.filter((w) => w.id !== id));
  }

  /** Public so the realtime service can re-sync after a WS event. */
  upsertCache(wo: WorkOrder): void {
    this.workOrdersSignal.update((list) => {
      const idx = list.findIndex((w) => w.id === wo.id);
      if (idx === -1) return [wo, ...list];
      const next = list.slice();
      next[idx] = wo;
      return next;
    });
  }

  /** Synchronous cache read, for the detail screen's optimistic seed before the API roundtrip lands. */
  getById(id: string): WorkOrder | undefined {
    return this.workOrdersSignal().find((w) => w.id === id);
  }

  clearError(): void {
    this.errorSignal.set(null);
  }

  /** Runs a write, caching its result; on failure records the hub's message in `error` and returns `null`. */
  private async mutate(action: Observable<WorkOrder | null>, fallback: string): Promise<WorkOrder | null> {
    this.errorSignal.set(null);
    try {
      const saved = this.cached(await firstValueFrom(action));
      if (!saved) {
        this.errorSignal.set(fallback);
      }
      return saved;
    } catch (error: unknown) {
      this.errorSignal.set(ApiError.fromHttpError(error).errors[0] ?? fallback);
      return null;
    }
  }

  private toWorkOrder(data: unknown): WorkOrder | null {
    return data === null || data === undefined ? null : this.createType(data);
  }

  private cached(wo: WorkOrder | null): WorkOrder | null {
    if (wo) {
      this.upsertCache(wo);
    }
    return wo;
  }
}
